import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import KpiCard from '../shared/KpiCard';
import { formatNum, CHART_COLORS } from '../shared/formatters';

class AnalyticsFinancial extends Component {
  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var data = this.props.data;
    if (!data) return <div className="tw-text-center tw-py-16 tw-text-[#999]">No data available.</div>;

    var dailyGmv = data.dailyGmv || [];
    var paymentSplit = data.paymentSplit || [];
    var offerRevenue = data.offerRevenue || [];

    var growthColor = data.gmvGrowth >= 0 ? '#10B981' : '#EF4444';
    var growthIcon = data.gmvGrowth >= 0 ? 'fa-arrow-up' : 'fa-arrow-down';
    var growthText = (data.gmvGrowth >= 0 ? '+' : '') + data.gmvGrowth + '%';

    // Format payment split for pie chart
    var paymentPie = paymentSplit.map(function(p) {
      return { name: (p.method || 'stripe').charAt(0).toUpperCase() + (p.method || 'stripe').slice(1), value: p.amount, count: p.count };
    });

    // Format offer vs regular for bar
    var offerBar = offerRevenue.map(function(o) {
      return { name: o.type === 'offer' ? 'Offer Sales' : 'Regular Sales', amount: o.amount, count: o.count };
    });

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-dollar" label="GMV" value={'$' + formatNum(data.gmv)} color="#D4A843"
            change={<span style={{ color: growthColor }}><i className={'fa ' + growthIcon} style={{ fontSize: 10 }} /> {growthText} vs prior</span>}
          />
          <KpiCard icon="fa-building" label="Gross Platform Revenue" value={'$' + formatNum((data.platformRevenue || 0) + (data.transactionFeeRevenue || 0))} color="#10B981" />
          <KpiCard icon="fa-shopping-cart" label="Orders" value={data.orderCount} color="#3B82F6" />
          <KpiCard icon="fa-calculator" label="Avg Order Value" value={'$' + formatNum(data.aov)} color="#8B5CF6" />
          <KpiCard icon="fa-undo" label="Refund Rate" value={data.refundRate + '%'} color="#EF4444"
            change={data.refundCount + ' refund' + (data.refundCount !== 1 ? 's' : '') + ' ($' + formatNum(data.refundAmount) + ')'}
          />
          <KpiCard icon="fa-clock-o" label="Avg Payout Time" value={data.avgPayoutDays + 'd'} color="#F59E0B" />
        </div>

        {/* Revenue Breakdown */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Revenue Breakdown</h3>
          <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-font-semibold tw-text-[#999]">Net Earnings <span className="tw-relative tw-inline-block tw-ml-0.5 tw-group"><i className="fa fa-info-circle" style={{ fontSize: 10, color: '#bbb', cursor: 'help' }} /><span className="tw-absolute tw-bottom-full tw-left-1/2 tw-mb-1.5 tw-hidden group-hover:tw-block tw-whitespace-nowrap tw-text-[10px] tw-text-white tw-px-2 tw-py-1 tw-rounded tw-z-50" style={{ backgroundColor: '#333', transform: 'translateX(-50%)' }}>Commission + Transaction Fees − Processor Fees</span></span></span>
              <span className="tw-text-lg tw-font-bold" style={{ color: '#10B981' }}>${formatNum(((data.platformRevenue || 0) + (data.transactionFeeRevenue || 0) - (data.estimatedProcessorFees || 0)))}</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-[#999]">Commission</span>
              <span className="tw-text-lg tw-font-bold" style={{ color: '#10B981' }}>${formatNum(data.platformRevenue)}</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-[#999]">Transaction Fees</span>
              <span className="tw-text-lg tw-font-bold" style={{ color: '#10B981' }}>${formatNum(data.transactionFeeRevenue || 0)}</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-[#999]">Est. Processor Fees</span>
              <span className="tw-text-lg tw-font-bold" style={{ color: '#EF4444' }}>-${formatNum(data.estimatedProcessorFees || 0)}</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-[#999]">Marketer Payouts</span>
              <span className="tw-text-lg tw-font-bold" style={{ color: '#F59E0B' }}>-${formatNum(data.marketerPayouts || 0)}</span>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-[#999]">Tax Collected <span style={{ color: '#999', fontStyle: 'italic' }}>(pass-through)</span></span>
              <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">${formatNum(data.taxCollected || 0)}</span>
            </div>
          </div>
        </div>

        {/* GMV Trend Chart */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">GMV Trend</h3>
          {dailyGmv.length > 0 ? (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={dailyGmv} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4A843" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#D4A843" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return '$' + v; }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={function(v, name) { return ['$' + Number(v).toFixed(2), name === 'gmv' ? 'GMV' : 'Orders']; }}
                  />
                  <Area type="monotone" dataKey="gmv" stroke="#D4A843" strokeWidth={2.5} fill="url(#gmvGrad)" dot={false} activeDot={{ r: 5, fill: '#D4A843' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No GMV data yet</div>
          )}
        </div>

        {/* Row: Payment Split + Offer vs Regular + Shipping */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Payment Method Split */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Payment Methods</h3>
            {paymentPie.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={paymentPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35} paddingAngle={2}>
                        {paymentPie.map(function(entry, i) { return <Cell key={'pay-' + i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                        itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }}
                        formatter={function(v) { return ['$' + Number(v).toFixed(2)]; }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-space-y-1 tw-mt-2">
                  {paymentPie.map(function(p, i) {
                    return (
                      <div key={'pl-' + i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                        <span className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-inline-block" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="tw-text-[#666] dark:tw-text-[#aaa]">{p.name}</span>
                        </span>
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">${formatNum(p.value)} ({p.count})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Offer vs Regular */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Offer vs Regular Sales</h3>
            {offerBar.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <BarChart data={offerBar} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#999' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return '$' + v; }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                        itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }}
                        formatter={function(v) { return ['$' + Number(v).toFixed(2)]; }} />
                      <Bar dataKey="amount" name="Revenue" radius={[4, 4, 0, 0]}>
                        {offerBar.map(function(e, i) { return <Cell key={'ob-' + i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-space-y-1 tw-mt-2">
                  {offerBar.map(function(o, i) {
                    return (
                      <div key={'ol-' + i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                        <span className="tw-text-[#666] dark:tw-text-[#aaa]">{o.name}</span>
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{o.count} orders</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data yet</div>
            )}
          </div>

          {/* Shipping Stats */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Shipping Stats</h3>
            <div className="tw-space-y-4 tw-mt-4">
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-sm tw-text-[#666] dark:tw-text-[#aaa]">Avg Shipping Cost</span>
                <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">${formatNum(data.avgShipping)}</span>
              </div>
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-sm tw-text-[#666] dark:tw-text-[#aaa]">Free Shipping %</span>
                <span className="tw-text-lg tw-font-bold" style={{ color: '#10B981' }}>{data.freeShippingPct}%</span>
              </div>
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-sm tw-text-[#666] dark:tw-text-[#aaa]">Avg Payout Hold</span>
                <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">{data.avgPayoutDays} days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AnalyticsFinancial;
